import { ROUTES } from '@/lib/routes';
import { MOBILE_TABS, moduleForPath, type AdminSection, type MobileTab, type ModuleId, type NavGroup, type NavItem } from '@/lib/navigation';

/**
 * RBAC on the client (dev/planning/general.md §4.12). The server is authoritative (server/rbac/routeRules.ts);
 * this only decides what to **show**: which pages the sidebar, Home, page search, New ▾ and Settings list, and the
 * "No access" page for a URL the user can't open.
 *
 * One table maps page paths to the area that opens them, mirroring the API rules. **First match wins**, so specific
 * paths go above their module's catch-all. Paths not listed (Home, Account, the parked AI chat) are open.
 */

export type Level = 'none' | 'read' | 'full';
export interface Permissions {
  roleId: string; roleName: string;
  grants: Record<string, Level>;
  switches: string[];
  administrator: boolean;
  breakGlass: boolean;
  /** An admin testing a role ("View as role"): the fields above are that role's. */
  viewAs?: { roleId: string; roleName: string } | null;
}

const RANK: Record<Level, number> = { none: 0, read: 1, full: 2 };
export const can = (p: Permissions | null | undefined, key: string, level: Exclude<Level, 'none'> = 'read') =>
  !p || RANK[p.grants[key] ?? 'none'] >= RANK[level];
export const hasSwitch = (p: Permissions | null | undefined, key: string) => !p || p.switches.includes(key);

type PageRule = [prefix: string, keys: string | string[], level?: 'read' | 'full'];

const PAGE_RULES: PageRule[] = [
  // PSA
  [ROUTES.psa.dashboards.root, 'psa.dashboards'],
  [ROUTES.psa.tickets.new, 'psa.tickets', 'full'],
  ['/psa/tickets', 'psa.tickets'],
  [ROUTES.psa.projects.new, 'psa.projects', 'full'],
  [ROUTES.psa.projects.all, 'psa.projects'],
  [ROUTES.psa.dispatch, 'psa.dispatch'],
  [ROUTES.psa.presales, 'psa.presales'],
  // The combined ticket + opportunity search (phones) stays in PSA.
  [ROUTES.psa.search, ['psa.tickets', 'sales.opportunities']],
  [ROUTES.psa.root, ['psa.tickets', 'psa.projects', 'psa.dispatch', 'psa.dashboards', 'psa.presales']],
  // Sales (opportunities + the Sales dashboard moved here from PSA 2026-11-04)
  [ROUTES.sales.opportunities.new, 'sales.opportunities', 'full'],
  [ROUTES.sales.opportunities.all, 'sales.opportunities'],
  [ROUTES.sales.dashboard, 'sales.dashboards'],
  [ROUTES.sales.newQuote, 'sales.quotes', 'full'],
  [ROUTES.sales.postSales, 'sales.postSales'],
  ['/sales/library', ['sales.templates', 'sales.quotes']],
  [ROUTES.sales.quotes, 'sales.quotes'],
  [ROUTES.sales.root, ['sales.quotes', 'sales.opportunities', 'sales.dashboards', 'sales.postSales', 'sales.templates']],
  // RMM
  ['/rmm/dashboards', 'rmm.dashboards'],
  [ROUTES.rmm.alerts, 'rmm.alerts'],
  [ROUTES.rmm.remote, 'rmm.remote'],
  [ROUTES.rmm.root, 'rmm.devices'],
  // Tenant management
  ['/cipp/dashboards', 'cipp.security'],
  [ROUTES.cipp.devices, 'cipp.devices'],
  [ROUTES.cipp.root, 'cipp.tenants'],
  // CSP
  [ROUTES.csp.upload, 'csp.upload', 'full'],
  [ROUTES.csp.mappings, 'csp.upload', 'full'],
  [ROUTES.csp.requests, 'csp.requests'],
  [ROUTES.csp.root, 'csp.renewals'],
  // Read-only modules
  [ROUTES.network.sophos.dashboard, 'network.sophos'],
  [ROUTES.network.unifi.dashboard, 'network.unifi'],
  [ROUTES.network.root, ['network.sophos', 'network.unifi']],
  [ROUTES.domains.root, 'domains.domains'],
  [ROUTES.docs.root, 'docs.itglue'],
  [ROUTES.backup.root, 'backup.saas'],
  [ROUTES.soc.root, 'soc.huntress'],
  // Insights
  [ROUTES.insights.find, 'insights.find'],
  ['/insights/devices', 'insights.find'],
  ['/insights/people', 'insights.find'],
  [ROUTES.insights.root, 'insights.coverage'],
  [ROUTES.companies, 'insights.orbit'],
  [ROUTES.dashboards, 'dashboards.custom'],
  // Administration (all Full)
  [ROUTES.admin.users, 'admin.users', 'full'],
  [ROUTES.admin.webhook, 'admin.webhook', 'full'],
  [ROUTES.admin.aiAssistant, 'admin.ai', 'full'],
  [ROUTES.admin.ai, 'admin.ai', 'full'],
  [ROUTES.admin.system, 'admin.integrations', 'full'],
  [ROUTES.admin.integrations, 'admin.integrations', 'full'],
  [ROUTES.admin.mappings, 'admin.mappings', 'full'],
  [ROUTES.admin.cippSettings, 'admin.settings', 'full'],
  [ROUTES.admin.cipp, 'admin.mappings', 'full'],
  [ROUTES.admin.networkSettings, 'admin.settings', 'full'],
  [ROUTES.admin.network, 'admin.mappings', 'full'],
  [ROUTES.admin.root, ['admin.settings', 'admin.users', 'admin.integrations', 'admin.mappings', 'admin.ai', 'admin.webhook'], 'full'],
];

const matches = (path: string, prefix: string) => path === prefix || path.startsWith(`${prefix}/`);

/** The rule a page path falls under (null = open to everyone). */
export function pageRule(path: string): { keys: string[]; level: 'read' | 'full' } | null {
  const p = path.split('?')[0];
  const r = PAGE_RULES.find(([prefix]) => matches(p, prefix));
  return r ? { keys: [r[1]].flat(), level: r[2] ?? 'read' } : null;
}

/** Whether the user can open a page. Unknown permissions (still loading) = yes; the server still checks. */
export function canOpen(p: Permissions | null | undefined, path: string): boolean {
  if (!p) return true;
  const r = pageRule(path);
  return !r || r.keys.some((k) => can(p, k, r.level));
}

/** Menus without the pages the user can't open; a group with no links left is dropped (its "Soon" items too). */
export function filterGroups(groups: NavGroup[], p: Permissions | null | undefined): NavGroup[] {
  if (!p) return groups;
  return groups
    .map((g) => ({ ...g, items: g.items.filter((i: NavItem) => i.kind !== 'link' || canOpen(p, i.to)) }))
    .filter((g) => g.items.some((i) => i.kind === 'link'));
}

/** Settings → admin sections without the pages the user can't open. */
export function filterAdminSections(sections: AdminSection[], p: Permissions | null | undefined): AdminSection[] {
  if (!p) return sections;
  return sections.map((s) => ({ ...s, items: s.items.filter((i) => canOpen(p, i.to)) })).filter((s) => s.items.length);
}

/**
 * A module's menu for this user, or [] when the module shouldn't be listed at all. A module needs at least one of
 * **its own** pages: a cross-module link (SOC's "Computers without Huntress" → Insights gaps) must not keep a module
 * the user can't open on screen. The Dashboards section is exempt: it gathers other modules' dashboards by design.
 */
export function filterModuleMenu(moduleId: ModuleId, groups: NavGroup[], p: Permissions | null | undefined): NavGroup[] {
  const visible = filterGroups(groups, p);
  if (!p || moduleId === 'dashboards') return visible;
  const ownPage = visible.some((g) => g.items.some((i) => i.kind === 'link' && moduleForPath(i.to.split('?')[0]) === moduleId));
  return ownPage ? visible : [];
}

/** The phone tabs for this role: the first four candidates it can open (MOBILE_TABS order). */
export function mobileTabsFor(p: Permissions | null | undefined, max = 4): MobileTab[] {
  const out: MobileTab[] = [];
  for (const t of MOBILE_TABS) {
    if (out.length >= max) break;
    if (!canOpen(p, t.to) || (t.needs && !can(p, t.needs.key, t.needs.level))) continue;
    // One opportunities tab at most (the read-only one is only a fallback).
    if (t.id === 'opps-read' && out.some((x) => x.id === 'opps')) continue;
    out.push(t);
  }
  return out;
}

/** The tab owning a path: longest `owns` prefix across the tabs shown; null = More. */
export function activeMobileTab(pathname: string, tabs: MobileTab[]): MobileTab | null {
  let best: { tab: MobileTab; len: number } | null = null;
  for (const t of tabs) for (const o of t.owns) {
    if ((pathname === o || pathname.startsWith(`${o}/`)) && (!best || o.length > best.len)) best = { tab: t, len: o.length };
  }
  return best?.tab ?? null;
}
